import { useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Check, ChevronLeft, ChevronRight, Star, Truck, ShieldCheck, RotateCcw, Phone, Plus, ShoppingBag,
} from "lucide-react";
import { api, formatUSD } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "@/components/ui/accordion";
import { ComparisonTable } from "@/components/ComparisonTable";
import { CtaBanner } from "@/components/sections/CtaBanner";
import { BRAND } from "@/data/content";
import { toast } from "sonner";

const TRUST = [
  { icon: Truck, label: "Free US shipping" },
  { icon: ShieldCheck, label: "Lifetime equipment warranty" },
  { icon: RotateCcw, label: "No long-term contract" },
  { icon: Phone, label: "24/7 US-based monitoring" },
];

function Gallery({ product }) {
  const gallery = product.gallery?.length ? product.gallery : [product.image];
  const [idx, setIdx] = useState(0);
  const touchX = useRef(null);
  const step = (n) => setIdx((i) => (i + n + gallery.length) % gallery.length);

  return (
    <div>
      <div
        className="relative aspect-square rounded-3xl overflow-hidden border border-brand-line bg-[radial-gradient(circle_at_50%_30%,#ffffff,#EDF1F6)]"
        data-testid="detail-gallery"
        onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (touchX.current == null) return;
          const dx = e.changedTouches[0].clientX - touchX.current;
          touchX.current = null;
          if (Math.abs(dx) > 40) step(dx < 0 ? 1 : -1);
        }}
      >
        {gallery.map((src, i) => (
          <img
            key={src}
            src={src}
            alt={`${product.name} — photo ${i + 1}`}
            className={`absolute inset-0 w-full h-full transition-opacity duration-300 ${
              i === 0 ? "object-contain p-10" : "object-cover"
            } ${i === idx ? "opacity-100" : "opacity-0"}`}
          />
        ))}
        {gallery.length > 1 && (
          <>
            <button
              onClick={() => step(-1)}
              aria-label="Previous photo"
              data-testid="detail-gallery-prev"
              className="btn-focus absolute left-3 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-white/95 text-brand-navy shadow-card hover:bg-white transition-colors"
            >
              <ChevronLeft className="w-6 h-6" strokeWidth={2.6} />
            </button>
            <button
              onClick={() => step(1)}
              aria-label="Next photo"
              data-testid="detail-gallery-next"
              className="btn-focus absolute right-3 top-1/2 -translate-y-1/2 grid place-items-center w-11 h-11 rounded-full bg-white/95 text-brand-navy shadow-card hover:bg-white transition-colors"
            >
              <ChevronRight className="w-6 h-6" strokeWidth={2.6} />
            </button>
          </>
        )}
      </div>
      {gallery.length > 1 && (
        <div className="mt-3 grid grid-cols-5 gap-2.5">
          {gallery.map((src, i) => (
            <button
              key={src}
              onClick={() => setIdx(i)}
              data-testid={`detail-thumb-${i}`}
              aria-label={`Show photo ${i + 1}`}
              className={`btn-focus relative aspect-square rounded-xl overflow-hidden border-2 transition-colors ${
                i === idx ? "border-brand-red" : "border-brand-line hover:border-brand-navy/40"
              }`}
            >
              <img src={src} alt="" loading="lazy" className={`w-full h-full ${i === 0 ? "object-contain p-1.5 bg-white" : "object-cover"}`} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ProductDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();

  const { data: product, isLoading, isError } = useQuery({
    queryKey: ["product", slug],
    queryFn: async () => {
      const { data } = await api.get(`/products/${slug}`);
      return data;
    },
    retry: 1,
  });

  const handleAdd = () => {
    addItem(product);
    toast.success(`${product.name} added to cart`, {
      description: "Free US shipping · No long-term contract",
    });
  };

  const handleBuyNow = () => {
    addItem(product);
    navigate("/cart");
  };

  if (isLoading) {
    return (
      <div className="pt-14 pb-24 bg-brand-bg min-h-screen" data-testid="detail-loading">
        <div className="container-x grid lg:grid-cols-2 gap-10 pt-10">
          <div className="aspect-square rounded-3xl bg-white border border-brand-line animate-pulse" />
          <div className="space-y-4 pt-4">
            <div className="h-5 w-32 rounded bg-white animate-pulse" />
            <div className="h-12 w-3/4 rounded bg-white animate-pulse" />
            <div className="h-24 rounded bg-white animate-pulse" />
            <div className="h-40 rounded bg-white animate-pulse" />
          </div>
        </div>
      </div>
    );
  }

  if (isError || !product) {
    return (
      <div className="py-32 text-center bg-brand-bg min-h-screen" data-testid="detail-not-found">
        <h1 className="font-display font-extrabold text-3xl text-brand-navy">Device not found</h1>
        <p className="mt-3 text-brand-ink2">That product doesn't exist or may have moved.</p>
        <Link to="/shop" className="btn-focus mt-6 inline-flex items-center gap-2 h-12 px-6 rounded-full bg-brand-red text-white font-bold hover:bg-brand-red-600 transition-colors">
          Browse all devices
        </Link>
      </div>
    );
  }

  return (
    <div data-testid="product-detail-page">
      {/* breadcrumb strip */}
      <section className="bg-brand-navy pt-10 pb-8">
        <div className="container-x">
          <nav className="flex items-center gap-2 text-sm text-white/70" data-testid="detail-breadcrumb">
            <Link to="/shop" className="btn-focus font-semibold hover:text-white transition-colors">Devices</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="font-semibold text-white">{product.name}</span>
          </nav>
        </div>
      </section>

      <section className="py-12 lg:py-16 bg-brand-bg">
        <div className="container-x grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <Gallery product={product} />

          <div>
            <span className="text-xs font-bold uppercase tracking-[0.16em] text-brand-red">{product.category}</span>
            <h1 className="mt-2 font-display font-black tracking-tight text-brand-navy text-4xl sm:text-5xl leading-[1.05]" data-testid="detail-name">
              {product.name}
            </h1>
            <div className="mt-3 flex items-center gap-2">
              <span className="flex text-brand-red">
                {[...Array(5)].map((_, i) => <Star key={i} className="w-5 h-5 fill-current" />)}
              </span>
              <span className="text-sm font-semibold text-brand-ink2">4.9 · trusted by 50,000+ families</span>
            </div>
            <p className="mt-4 text-lg text-brand-navy font-semibold">{product.tagline}</p>
            <p className="mt-2 text-brand-ink2 leading-relaxed">{product.description}</p>

            <ul className="mt-6 space-y-2.5">
              {product.features.map((f, i) => (
                <li key={i} className="flex items-start gap-2.5 text-[15px] text-brand-navy">
                  <Check className="w-5 h-5 shrink-0 text-brand-red mt-0.5" strokeWidth={2.6} />
                  <span>{f}</span>
                </li>
              ))}
            </ul>

            <div className="mt-7 rounded-2xl bg-white border border-brand-line p-5 shadow-card">
              <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                  <span className="block text-xs text-brand-ink2 font-semibold">One-time purchase · Free US shipping</span>
                  <span className="font-display font-extrabold text-4xl text-brand-navy" data-testid="detail-price">
                    {formatUSD(product.price)}
                  </span>
                </div>
                <span className="px-3 py-1.5 rounded-full bg-brand-navy/5 text-brand-navy text-xs font-bold">{product.highlight}</span>
              </div>
              <div className="mt-4 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleAdd}
                  data-testid="detail-add-to-cart"
                  className="btn-focus flex-1 inline-flex items-center justify-center gap-2 h-14 px-6 rounded-full bg-brand-red text-white font-bold text-lg hover:bg-brand-red-600 transition-colors"
                >
                  <Plus className="w-5 h-5" strokeWidth={2.8} /> Add to cart
                </button>
                <button
                  onClick={handleBuyNow}
                  data-testid="detail-buy-now"
                  className="btn-focus flex-1 inline-flex items-center justify-center gap-2 h-14 px-6 rounded-full bg-brand-navy text-white font-bold text-lg hover:bg-brand-navy-700 transition-colors"
                >
                  <ShoppingBag className="w-5 h-5" strokeWidth={2.4} /> Buy now
                </button>
              </div>
              <a href={BRAND.phoneHref} className="btn-focus mt-3 inline-flex items-center gap-2 text-sm font-bold text-brand-navy hover:text-brand-red transition-colors">
                <Phone className="w-4 h-4" /> Questions? Talk to a specialist — {BRAND.phone}
              </a>
            </div>

            <div className="mt-6 grid grid-cols-2 gap-3">
              {TRUST.map((t) => (
                <span key={t.label} className="inline-flex items-center gap-2.5 text-sm font-semibold text-brand-navy">
                  <span className="grid place-items-center w-9 h-9 rounded-full bg-white border border-brand-line shrink-0">
                    <t.icon className="w-5 h-5 text-brand-red" strokeWidth={2.2} />
                  </span>
                  {t.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* specs */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="container-x">
          <span className="eyebrow text-brand-red">
            <span className="inline-block h-px w-7 bg-brand-red" />
            Full Specifications
          </span>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-brand-navy">
            Everything under the hood.
          </h2>
          <div className="mt-8 grid md:grid-cols-2 gap-x-12" data-testid="detail-specs">
            {(product.specs || []).map((s) => (
              <div key={s.label} className="flex gap-4 py-3.5 border-b border-brand-line">
                <span className="w-40 shrink-0 font-bold text-brand-navy text-sm pt-0.5">{s.label}</span>
                <span className="text-brand-ink2 text-[15px]">{s.value}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* reviews */}
      {(product.reviews || []).length > 0 && (
        <section className="py-14 lg:py-20 bg-brand-bg">
          <div className="container-x">
            <span className="eyebrow text-brand-red">
              <span className="inline-block h-px w-7 bg-brand-red" />
              Real Stories
            </span>
            <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-brand-navy">
              What families say about the {product.name}.
            </h2>
            <div className="mt-8 grid md:grid-cols-3 gap-5" data-testid="detail-reviews">
              {product.reviews.map((r, i) => (
                <div key={i} className="rounded-2xl bg-white border border-brand-line p-6 shadow-card flex flex-col" data-testid={`detail-review-${i}`}>
                  <span className="flex gap-0.5">
                    {[...Array(5)].map((_, j) => (
                      <Star key={j} className={`w-4 h-4 ${j < r.rating ? "text-brand-red fill-current" : "text-slate-300"}`} />
                    ))}
                  </span>
                  <p className="mt-3 font-display font-bold text-lg text-brand-navy">{r.title}</p>
                  <p className="mt-2 text-brand-ink2 leading-relaxed text-[15px] flex-1">“{r.text}”</p>
                  <p className="mt-4 text-sm font-bold text-brand-navy">{r.name} <span className="font-semibold text-brand-ink2">· {r.location}</span></p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* faqs */}
      <section className="py-14 lg:py-20 bg-white">
        <div className="container-x max-w-4xl">
          <span className="eyebrow text-brand-red">
            <span className="inline-block h-px w-7 bg-brand-red" />
            Common Questions
          </span>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-brand-navy">
            {product.name}, answered.
          </h2>
          <Accordion type="single" collapsible className="mt-8 space-y-3" data-testid="detail-faqs">
            {(product.faqs || []).map((f, i) => (
              <AccordionItem
                key={i}
                value={`faq-${i}`}
                className="rounded-2xl bg-white border border-brand-line px-5 data-[state=open]:shadow-card"
              >
                <AccordionTrigger data-testid={`detail-faq-${i}`} className="text-left font-bold text-brand-navy hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-brand-ink2 leading-relaxed text-[15px]">
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* compare */}
      <section className="py-14 lg:py-20 bg-brand-bg">
        <div className="container-x">
          <span className="eyebrow text-brand-red">
            <span className="inline-block h-px w-7 bg-brand-red" />
            Compare Devices
          </span>
          <h2 className="mt-4 font-display font-extrabold text-3xl sm:text-4xl text-brand-navy">
            How it stacks up against the family.
          </h2>
          <div className="mt-8">
            <ComparisonTable highlight={product.slug} />
          </div>
        </div>
      </section>

      <CtaBanner />
    </div>
  );
}
